import React, { useEffect } from 'react';
import PhoneInput from 'react-phone-input-2';
import { useField, useFormikContext } from 'formik';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import FormFieldError from '@components/shared/Form/FormFieldError';
import { P } from '@components/shared/Typography';
import { useCountry } from '@hooks/useCountry';

interface InputPhoneProps {
  name: string;
  label?: string;
  description?: string;
  style?: React.CSSProperties;
  onChange?: (value: string) => void;
  required?: boolean;
}

interface StyledPProps {
  $hasError?: boolean;
}

export default function InputPhone(props: InputPhoneProps) {
  const { label, description, style, onChange, required, ...fieldProps } = props;
  const { country } = useCountry();
  const [field, meta] = useField({ type: 'tel', ...fieldProps });
  const { setFieldTouched } = useFormikContext();
  const hasError = meta.touched && meta.error;

  useEffect(() => {
    if (required) {
      setFieldTouched(field.name, true, false);
    }
  }, [required, field.name, setFieldTouched]);

  const handleChange = (value: string) => {
    field.onChange({ target: { name: field.name, value } });
    setFieldTouched(field.name, true, false);
    onChange?.(value);
  };

  return (
    <InputWrapper style={style}>
      {label && <PStyled $hasError={!!hasError}>{label}</PStyled>}
      {description && <Description>{description}</Description>}
      <PhoneInput
        country={country.toLowerCase()}
        value={field.value}
        onChange={handleChange}
        isValid={!hasError}
        enableSearch
        inputClass="phone-input"
        buttonClass="phone-button"
        dropdownClass="phone-dropdown"
        inputProps={{
          role: 'phone',
          name: field.name,
          onBlur: field.onBlur
        }}
      />
      {hasError && <FormFieldError message={meta.error || ''} style={{ marginTop: 10 }} />}
    </InputWrapper>
  );
}

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    margin-bottom: 15px;
  }

  &:last-child {
    margin-bottom: 0;
  }

  .react-tel-input {
    display: flex;
    margin-top: 10px;
    height: 50px;
    flex-direction: row-reverse;
    gap: 20px;
  }

  div {
    .flag-dropdown.open {
      background: ${Colors.cream};
      .selected-flag {
        background: ${Colors.cream};
      }
    }

    .phone-input {
      border: 1px solid ${Colors.gold}!important;
      background: ${Colors.cream}!important;
      color: ${Colors.navyBlue};
      font-family: 'BrownStd';
      border-radius: 2px !important;
      margin-left: 0;
      padding: 15px;
      background: transparent;
      height: auto;
      width: auto;
      flex-grow: 1;

      &.invalid-number {
        border-color: ${Colors.red}!important;
      }
    }

    .phone-button {
      border: 1px solid ${Colors.gold}!important;
      border-radius: 2px;
      background: transparent;
      padding-inline: 20px;
      position: relative;

      .selected-flag {
        background: ${Colors.cream};
        padding: 0;
        .arrow {
          margin-left: 10px;
        }
      }
    }

    .phone-dropdown {
      box-shadow: none;
      border: 1px solid ${Colors.gold};

      .search {
        background: ${Colors.cream};
        margin: 0;
        padding: 0;
        border-bottom: 1px solid ${Colors.ecru};

        .search-box {
          width: 100%;
          height: auto;
          font-family: 'BrownStd';
          background: #0000;
          border: 0;
          margin: 0;
          text-transform: capitalize;
          display: block;
          padding: 10px 15px;
          font-size: 14px;
        }

        .search-emoji {
          display: none;
        }
      }

      .country {
        padding: 6px 15px;
        font-size: 12px;

        :hover {
          background: ${Colors.ecru};
        }
      }

      position: absolute;
      left: 0;

      max-height: 300px;
      background: ${Colors.cream};
      font-family: 'BrownStd';
      font-style: normal;
      font-weight: 400;
      font-size: 14px;
      line-height: 150%;

      @media (max-width: 768px) {
        font-size: 13px;
      }
    }
  }
`;

const PStyled = styled(P)<StyledPProps>`
  font-weight: 700;
  font-size: 16px;
  line-height: 150%;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0;
  text-align: left;
  margin-top: 0;
  color: ${(props) => (props.$hasError ? Colors.red : 'inherit')};

  &:first-child {
    margin-top: 0;
  }
`;

const Description = styled(PStyled)`
  font-weight: 400;
`;
